import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import DropdownContext from "@restart/ui/DropdownContext";
import classNames from "classnames";
import React, {useContext} from "react";
import {DropdownMenu as BSDropdownMenu} from "react-bootstrap";

import {DropdownMenuProps} from "@/components/dropdown/subComponents/DropdownMenu/DropdownMenu.types.ts";

import styles from "./DropdownMenu.module.scss";

const PortaledDropdownMenu = React.forwardRef(
  ({children, className = "", align, ...props}: DropdownMenuProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const ctx = useContext(DropdownContext);
    const show = ctx?.show ?? false;
    const toggleElement = ctx?.toggleElement ?? null;
    const alignEnd = align === "end";

    const ctxPlacement = ctx?.placement;
    const placement: Placement =
      ctxPlacement && ctxPlacement !== "auto" ? (ctxPlacement as Placement) : alignEnd ? "bottom-end" : "bottom-start";

    const {refs, floatingStyles, context} = useFloating({
      open: show,
      onOpenChange: (open, event) => ctx?.toggle?.(open, event as React.SyntheticEvent | Event | undefined),
      placement,
      strategy: "fixed",
      whileElementsMounted: autoUpdate,
      elements: {reference: toggleElement ?? undefined},
      middleware: [offset(2), flip(), shift({padding: 8})],
    });

    const dismiss = useDismiss(context);
    const {getFloatingProps} = useInteractions([dismiss]);

    if (!show) return null;

    /* eslint-disable no-unused-vars */
    const {
      as: _as,
      show: _show,
      renderOnMount: _r,
      flip: _flip,
      popperConfig: _pc,
      rootCloseEvent: _rce,
      ...domProps
    } = props as DropdownMenuProps & {as?: unknown};
    /* eslint-enable no-unused-vars */

    return (
      <FloatingPortal>
        <div
          {...getFloatingProps(domProps as React.HTMLProps<HTMLDivElement>)}
          ref={(node) => {
            refs.setFloating(node);
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
            ctx?.setMenu?.(node);
          }}
          style={{...floatingStyles, ...domProps.style}}
          className={classNames("dropdown-menu show", styles.DropdownMenu, className)}
        >
          {children}
        </div>
      </FloatingPortal>
    );
  }
);
PortaledDropdownMenu.displayName = "PortaledDropdownMenu";

const DropdownMenu = React.forwardRef(
  (
    {children, className = "", portal = false, ...props}: DropdownMenuProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    if (portal) {
      return (
        <PortaledDropdownMenu ref={ref} className={className} {...props}>
          {children}
        </PortaledDropdownMenu>
      );
    }

    return (
      <BSDropdownMenu className={classNames(styles.DropdownMenu, className)} ref={ref} {...props}>
        {children}
      </BSDropdownMenu>
    );
  }
);

DropdownMenu.displayName = "DropdownMenu";
export {DropdownMenu};
